'use client';

import { useCallback, useRef, useEffect, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
//
import { Button, ButtonType } from '@nx-frontend/shared/ui/button';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { $ModalStore, closeModalEvent, openModalEvent } from '@nx-frontend/shared/ui/modal';
import { getIsAddonWasPurshasedEarly } from '@nx-frontend/shared/api/webnotes/get-is-addon-was-purchased-early';
import { generalEnv } from '@nx-frontend/shared/env';
import { $addonAiPrice, loadAddonAiPriceEvent } from '@nx-frontend/shared/store/addon-ai-price';
import { $app } from '@nx-frontend/shared/store/app';
import { $billingInfo } from '@nx-frontend/shared/store/billing-info/billing-info';
import { $currentOrganization } from '@nx-frontend/shared/store/current-organization/current-organization';
import { $orgCoupons } from '@nx-frontend/shared/store/org-coupons';
import { $orgInfo } from '@nx-frontend/shared/store/org-info';
import { $unleashFlags } from '@nx-frontend/shared/store/unleash-flags';
import { BillingAddonCode, PaywallPopupReason, PopupType, UnlockAIPopupPayload } from '@nx-frontend/shared/types';
import { GetOpenUnlockAIPopupProps } from '@nx-frontend/shared/types';
import { getOpenAddonPopup } from './addon-popup';
import { getOpenPaywallPopup, hasMaxSubscriptionPlan } from './paywall-popup';
import { getOpenUnlockAIBillingPopup } from './unlock-ai-billing-popup';

const modalContainerStyles = 'bg-unlock-ai-popup rounded-lg';

export const getOpenUnlockAIPopup = ({ openModal, payload }: GetOpenUnlockAIPopupProps) => {
  openModal({
    content: PopupType.UNLOCK_AI,
    showCloseButton: false,
    modalContainerStyles,
    payload,
    id: 0,
  });
};

export function UnlockAIPopup() {
  const [
    app,
    currentOrganization,
    billingInfo,
    addonAiPrice,
    modalStore,
    openModal,
    closeModal,
    loadAddonAiPrice,
    unleashFlags,
    orgInfo,
    orgCoupons,
  ] = useUnit([
    $app,
    $currentOrganization,
    $billingInfo,
    $addonAiPrice,
    $ModalStore,
    openModalEvent,
    closeModalEvent,
    loadAddonAiPriceEvent,
    $unleashFlags,
    $orgInfo,
    $orgCoupons,
  ]);
  const t = useTranslations('features');

  const openNewAddonPupup = useCallback(async () => {
    const isMaxSubscriptionPlan = hasMaxSubscriptionPlan(orgInfo, billingInfo, orgCoupons);
    const isAddonWasPurshasedEarlyOrMaxPlan = !isMaxSubscriptionPlan
      ? await getIsAddonWasPurshasedEarly({
          gatewayUrl: generalEnv.gatewayUrl,
          subscriptionId: billingInfo.subscriptionId,
          addon: BillingAddonCode.AI,
          sessionId: app.sessionId,
        })
      : true;
    if (!isAddonWasPurshasedEarlyOrMaxPlan) {
      getOpenPaywallPopup({
        openModal,
        payload: {
          payloadType: PopupType.PAYWALL,
          mode: 'upgrade',
          title: t('paywall.title_upgrade_now'),
          reason: PaywallPopupReason.AI_LIMIT,
          orgId: currentOrganization.globalId,
        },
      });
    } else {
      getOpenAddonPopup({
        openModal,
        payload: {
          payloadType: PopupType.ADDON,
          code: BillingAddonCode.AI,
        },
      });
    }
  }, [app.sessionId, billingInfo, currentOrganization.globalId, openModal, orgCoupons, orgInfo, t]);

  useEffect(() => {
    closeModal();
    setTimeout(() => {
      openNewAddonPupup();
    }, 0);
  }, [closeModal, openNewAddonPupup, unleashFlags]);

  const usage = useMemo(() => {
    let max = 20;
    let current = 20;
    const modal = modalStore.find((modal) => modal.payload && modal.payload.payloadType === PopupType.UNLOCK_AI);
    const payload = modal?.payload as UnlockAIPopupPayload;
    if (payload?.usage) {
      max = payload.usage.max || max;
      current = payload.usage.current || current;
    }
    return { max, current };
  }, [modalStore]);

  useEffect(() => {
    return;
    if (!addonAiPrice.isLoaded && billingInfo.subscriptionId) {
      // lazy load addon ai prices
      loadAddonAiPrice({
        orgId: currentOrganization.globalId,
        subscriptionId: billingInfo.subscriptionId,
      });
    }
  }, [currentOrganization, billingInfo, addonAiPrice.isLoaded, loadAddonAiPrice, unleashFlags]);

  const nimUnlockAIRef = useRef<HTMLDivElement>(null);

  const confirmClick = useCallback(async () => {
    if (addonAiPrice.isLoaded) {
      // waiting until ai limit popup close then open ai billing confirm popup
      setTimeout(() => {
        getOpenUnlockAIBillingPopup({
          openModal,
          payload: {
            payloadType: PopupType.UNLOCK_AI_BILLING,
          },
        });
      }, 250);
      closeModal();
    }
  }, [openModal, closeModal, addonAiPrice.isLoaded]);

  const bookDemoClick = useCallback(async () => {
    window.open(generalEnv.bookDemoUrl + '/?ws=ai_billing_popup', '_blank');
    closeModal();
  }, [closeModal]);

  return (
    <div
      data-cy="unlock-ai-popup"
      className="px-2 py-[0] w-[970px] min-w-[970px] min-h-[400px] relative nimbus-popup-content mx-1"
      ref={nimUnlockAIRef}
    >
      <div className="flex flex-col relative">
        <div
          className="cursor-pointer mb-2 text-gray-400 hover:text-gray-700 absolute -top-[17px] -right-[15px]"
          style={{ width: 18, height: 18 }}
          onClick={closeModal}
        >
          <Icon icon={IconsMap.CLOSE} />
        </div>
        <div
          data-cy="unlock-ai-popup-title"
          className="items-center basis-full w-full text-center text-3xl font-semibold text-white mb-[32px]"
        >
          Work Your Magic: Unlock All AI Powers Now!
        </div>
        <div className="flex">
          <div className="w-full mr-[20px]">
            <div className="bg-[#FADBD7] m-h-[59px] pt-[14px] pb-[16px] px-[16px] flex flex-col align-middle mb-[16px] rounded-lg">
              <div className="flex justify-between m-h-[21px] mb-[8px] text-sm">
                <div>
                  Ooops!{' '}
                  <span role="img" aria-label="sad">
                    😢
                  </span>
                </div>
                <div>
                  <span className="font-semibold">
                    {usage.current}/{usage.max}{' '}
                  </span>
                  AI responses used
                </div>
              </div>
              <div className="w-full bg-[#E35141] h-[6px] rounded-lg"></div>
            </div>
            <div className="flex flex-row bg-white rounded-lg px-[24px] pb-[32px] pt-[20px] min-h-[222px]">
              <div className="mr-[14px]">
                <div className="mt-[8px]">
                  <div className="py-[22px] text-xl font-semibold">Go Unlimited FuseBase AI</div>
                </div>
                <div className="flex flex-row align-middle mb-[16px]">
                  <div className="text-3xl font-semibold text-[#176B87] mr-[8px]">$7</div>
                  <div className="text-xs text-black/30 pt-[2px]">
                    <div>per member per month</div>
                    <div>with annual plan</div>
                  </div>
                </div>
                <div>
                  <Button
                    type={ButtonType.FLAT}
                    onClick={confirmClick}
                    bg="bg-[#002730] hover:bg-[#002730] active:bg-[#002730]"
                    radius="rounded"
                    data-cy={'unlock-ai-get-addon-btn-' + (addonAiPrice.isLoaded ? 'price-loaded' : 'price-not-loaded')}
                    className="m-h-[36px] h-[36px] w-[147px]"
                  >
                    Get AI addon
                  </Button>
                </div>
              </div>
              <div>
                <img
                  src="/assets/img/unlock-ai-buttons.svg"
                  width={283}
                  height={187}
                  className="min-h-[187px]"
                  alt=""
                />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-lg min-w-[318px] w-[318px] p-[24px]">
            <div className="mb-[10px]">
              <img
                src="/assets/img/unlock-ai-book-call.svg"
                width={127}
                height={132}
                className="rounded-sm min-h-[132px]"
                alt=""
              />
            </div>
            <div>
              <Button
                type={ButtonType.FLAT}
                onClick={bookDemoClick}
                bg="bg-[#002730] hover:bg-[#002730] active:bg-[#002730]"
                radius="rounded"
                isFull={true}
                data-cy="unlock-ai-book-call-btn"
                className="m-h-[30px] h-[30px]"
              >
                Book a demo
              </Button>
            </div>
            <div className="text-lg text-black font-semibold mb-[8px] whitespace-nowrap">
              <span>Are You Ready to </span>
              <span className="text-[#176B87]">Wow Clients?</span>
            </div>
            <div className="text-sm mb-[10px]">
              Book a call to see how tailored portals increase client satisfaction.
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
